'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { ShoppingCart, ArrowRight } from 'lucide-react'
import { useCartStore } from '@/store/cartStore'
import { formatPrice } from '@/lib/utils'

/* Shopping surfaces where a persistent cart bar helps conversion. */
export function isShoppingPage(pathname: string): boolean {
  return (
    pathname.startsWith('/winkel') ||
    pathname.startsWith('/aanbiedingen') ||
    pathname.startsWith('/pasen') ||
    pathname.startsWith('/sinterklaas') ||
    pathname.startsWith('/campagne')
  )
}

/* Approximate rendered height of the bar (px) incl. its top/bottom padding —
   used by the Toaster to lift toasts above the bar on mobile. */
export const STICKY_CART_BAR_HEIGHT = 76

/**
 * Slim, thumb-reachable cart summary fixed to the bottom on mobile/tablet.
 * Shows item count + running total and opens the cart drawer on tap.
 * Hidden on desktop (the navbar cart is always visible there), when the
 * cart is empty, while the drawer is open, and off shopping pages.
 */
export default function StickyCartBar() {
  const pathname = usePathname()
  const { isOpen, openCart, totalItems, totalPrice } = useCartStore()
  const [mounted, setMounted] = useState(false)

  useEffect(() => { setMounted(true) }, [])

  const count = mounted ? totalItems() : 0
  const total = mounted ? totalPrice() : 0
  const visible = mounted && count > 0 && !isOpen && isShoppingPage(pathname)

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 320 }}
          className="lg:hidden fixed inset-x-0 bottom-0 z-[70] px-3"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', paddingTop: 4 }}
        >
          <button
            onClick={openCart}
            className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left active:scale-[0.99] transition-transform"
            style={{ background: '#8B4513', color: '#FDF6EC', boxShadow: '0 8px 30px rgba(44,24,16,0.35)' }}
            aria-label={`Winkelwagen openen: ${count} ${count === 1 ? 'item' : 'items'}, totaal ${formatPrice(total)}`}
          >
            {/* Cart icon + count badge */}
            <span className="relative shrink-0">
              <ShoppingCart size={22} />
              <span
                className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold leading-none"
                style={{ background: '#DAA520', color: '#2C1810' }}
              >
                {count > 99 ? '99+' : count}
              </span>
            </span>

            <span className="flex-1 min-w-0">
              <span className="block text-[11px] font-medium" style={{ color: '#F4D8BC' }}>
                {count} {count === 1 ? 'item' : 'items'} in winkelwagen
              </span>
              <span className="block text-base font-bold leading-tight">{formatPrice(total)}</span>
            </span>

            <span className="flex items-center gap-1.5 shrink-0 rounded-xl px-3 py-1.5 text-sm font-bold" style={{ background: 'rgba(253,246,236,0.16)' }}>
              Bekijk
              <ArrowRight size={16} />
            </span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
